import { useState, useEffect } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { AppWatch } from '@wajeez-web/core/api-base';
import CohortGraph from 'react-cohort-graph';
import { ChartHeader } from '../components/ChartHeader';
import {
  useAppwatchSelector,
  useAppwatchDispatch,
  ContentAnalysisActions,
} from '@wajeez-web/stores/appwatch';
import { Card } from '@wajeez-web/components/ui';
import { ChartDisplay } from '@wajeez-web/components/ui';
import moment from 'moment';
import { getDateOfWeek } from '@wajeez-web/core/utils';
import { UserActiveRetentionAfterFirstSessionChart } from '../components/UserActivityCharts';
import FiltersSection from '../components/FiltersSection';

const UserActivityAnalysisPage = () => {
  const [dates, setDates] = useState<Date[]>([
    new Date(Date.now() - 30 * 6 * 24 * 3600 * 1000),
    new Date(),
  ]);
  const dispatch = useAppwatchDispatch();
  const {
    dailyActiveUsersCount,
    dailyActiveUsersCountStatus,
    weeklyActiveReadersData,
    weeklyActiveReadersStatus,
    monthlyActiveReadersData,
    monthlyActiveReadersStatus,
  } = useAppwatchSelector((c) => c.contentAnalysis);

  useEffect(() => {
    dispatch(
      ContentAnalysisActions.getDailyActiveUsersCountAsync({
        fromDate: dates[0].toISOString(),
        toDate: dates[1].toISOString(),
        plan: AppWatch.PlanType.All,
      })
    );
    dispatch(
      ContentAnalysisActions.getWeeklyActiveUsersAsync({
        fromDate: dates[0].toISOString(),
        toDate: dates[1].toISOString(),
        plan: AppWatch.PlanType.All,
      })
    );
    dispatch(
      ContentAnalysisActions.getMonthlyActiveUsersAsync({
        fromDate: dates[0].toISOString(),
        toDate: dates[1].toISOString(),
        plan: AppWatch.PlanType.All,
      })
    );
  }, [dates]);

  return (
    <PageLayoutsContainer
      title={`User Activity Analysis`}
      withoutRightPadding={false}
    >
      <div className="space-y-6 ">
        <FiltersSection
          dates={dates}
          setDates={setDates}
          autoSelectDatesDirection="horizontal"
        />

        <div className="grid grid-cols-2  gap-4">
          <div className="col-span-2">
            {' '}
            <Card>
              <div className="space-y-2">
                <ChartHeader
                  dates={dates}
                  title="Daily Active Readers"
                  plansFilterAction={(plan) => {
                    dispatch(
                      ContentAnalysisActions.getDailyActiveUsersCountAsync({
                        fromDate: dates[0].toISOString(),
                        toDate: dates[1].toISOString(),
                        plan: plan,
                      })
                    );
                  }}
                />

                <ChartDisplay
                  loading={dailyActiveUsersCountStatus === 'loading'}
                  xAxistype="datetime"
                  chartType="area"
                  formatter={(value, time) => {
                    return (
                      moment
                        .utc(value)
                        .local()
                        .locale('en')
                        .format('DD/MM/YYYY') || value
                    );
                  }}
                  seriesData={[
                    {
                      name: 'Readers',
                      type: 'area',
                      data: dailyActiveUsersCount.map((d) => ({
                        x: d.Date || '',
                        y: d.Count || '',
                      })),
                    },
                  ]}
                />
              </div>
            </Card>
          </div>{' '}
          <div className="col-span-2 md:col-span-1">
            {' '}
            <Card>
              <div className="space-y-2">
                <ChartHeader
                  dates={dates}
                  title="Weekly Active Readers"
                  plansFilterAction={(plan) => {
                    dispatch(
                      ContentAnalysisActions.getWeeklyActiveUsersAsync({
                        fromDate: dates[0].toISOString(),
                        toDate: dates[1].toISOString(),
                        plan: plan,
                      })
                    );
                  }}
                />

                <ChartDisplay
                  loading={weeklyActiveReadersStatus === 'loading'}
                  tooltip={{
                    custom: function ({
                      series,
                      seriesIndex,
                      dataPointIndex,
                      w,
                    }) {
                      return (
                        '<div dir="rtl" class="p-2  min-w-[100px] flex-col ">' +
                        '<div class="flex space-s-2 items-center">' +
                        '<div class="w-3 h-3 bg-primary rounded-full">' +
                        '</div>' +
                        '<div class="text-sm font-bold text-gray-700 ml-2 flex">' +
                        'Week: ' +
                        '<span>' +
                        weeklyActiveReadersData[dataPointIndex].week +
                        '</span>' +
                        '</div>' +
                        '</div>' +
                        '<div class="flex space-s-2 items-center">' +
                        '<div class="w-3 h-3 bg-primary rounded-full"/>' +
                        '</div>' +
                        '<div class="text-sm font-bold text-gray-700 ml-2 flex">  ' +
                        'Year: ' +
                        '<span>' +
                        weeklyActiveReadersData[dataPointIndex].year +
                        '</span>' +
                        '</div>' +
                        '</div>' +
                        '<div class="flex space-s-2 items-center">' +
                        '<div class="w-3 h-3 bg-primary rounded-full"/>' +
                        '</div>' +
                        '<div class="text-sm font-bold text-gray-700 ml-2 flex space-s-2">  ' +
                        'Readers: ' +
                        '<span>' +
                        +series[seriesIndex][dataPointIndex] +
                        '</span>' +
                        '</div>' +
                        '</div>' +
                        '</div>'
                      );
                    },
                  }}
                  formatter={(value, time) => {
                    return (
                      moment
                        .utc(value)
                        .local()
                        .locale('en')
                        .format('DD/MM/YYYY') || value
                    );
                  }}
                  chartType="area"
                  seriesData={[
                    {
                      type: 'area',
                      data: weeklyActiveReadersData.map((d) => ({
                        x:
                          getDateOfWeek(
                            Number(d.week) || 0,
                            Number(d.year) || 0
                          ) || '',
                        y: d.count || '',
                      })),
                    },
                  ]}
                />
              </div>
            </Card>
          </div>
          <div className="col-span-2 md:col-span-1">
            {' '}
            <Card>
              <div className="space-y-2">
                <ChartHeader
                  dates={dates}
                  title="Monthly Active Readers"
                  plansFilterAction={(plan) => {
                    dispatch(
                      ContentAnalysisActions.getMonthlyActiveUsersAsync({
                        fromDate: dates[0].toISOString(),
                        toDate: dates[1].toISOString(),
                        plan: plan,
                      })
                    );
                  }}
                />

                <ChartDisplay
                  loading={monthlyActiveReadersStatus === 'loading'}
                  chartType="area"
                  seriesData={[
                    {
                      type: 'area',
                      name: 'Readers',
                      data: monthlyActiveReadersData.map((d) => ({
                        x: `${d.month}/${d.year}` || '',
                        y: d.total_value || '',
                      })),
                    },
                  ]}
                />
              </div>
            </Card>
          </div>
          <UserActiveRetentionAfterFirstSessionChart dates={dates} />
        </div>
      </div>
    </PageLayoutsContainer>
  );
};

export default UserActivityAnalysisPage;
